/**
 * Resolve ROI form / custom-variable values by uuid OR field ID (RadioButton1, etc.).
 * Admin logic often references puck uuid; radios/checkboxes store under field ID.
 */

function isMeaningfulValue(value) {
  return value !== undefined && value !== null && value !== "";
}

function cacheValueForEntry(entry, mapKey, caches) {
  if (!entry || !caches) {
    return undefined;
  }

  const fieldId = entry.fieldId || mapKey;
  const radioCache = caches.radio?.[String(fieldId)];
  if (
    (entry.type === "Radio" || entry.type === "NestedRadioGroup") &&
    radioCache &&
    isMeaningfulValue(radioCache.value)
  ) {
    return radioCache.value;
  }

  if (String(mapKey).endsWith("__child")) {
    const parentFieldId = String(mapKey).replace(/__child$/, "");
    const nestedCache = caches.nestedRadioChild?.[parentFieldId];
    if (nestedCache && isMeaningfulValue(nestedCache.value)) {
      return nestedCache.value;
    }
  }

  return undefined;
}

/**
 * Find roiActionArr entry by primary key, componentUuid, or fieldId.
 */
export function resolveRoiVariableEntry(roiMap, key, caches = {}) {
  if (!roiMap || key === undefined || key === null || String(key).trim() === "") {
    return { entry: null, resolvedKey: null, value: undefined };
  }

  const lookup = String(key);

  const buildResult = (mapKey, entry) => {
    if (!entry) {
      return null;
    }
    const cached = cacheValueForEntry(entry, mapKey, caches);
    const value = isMeaningfulValue(cached) ? cached : entry.value;
    return { entry, resolvedKey: mapKey, value };
  };

  if (roiMap[lookup]) {
    const direct = buildResult(lookup, roiMap[lookup]);
    if (direct) {
      return direct;
    }
  }

  for (const mapKey of Object.keys(roiMap)) {
    const entry = roiMap[mapKey];
    if (!entry) {
      continue;
    }
    const uuid = entry.componentUuid ? String(entry.componentUuid) : "";
    const fieldId = entry.fieldId ? String(entry.fieldId) : "";
    if (uuid === lookup || fieldId === lookup || mapKey === lookup) {
      const resolved = buildResult(mapKey, entry);
      if (resolved) {
        return resolved;
      }
    }
  }

  return { entry: roiMap[lookup] || null, resolvedKey: roiMap[lookup] ? lookup : null, value: roiMap[lookup]?.value };
}

export function getRoiVariableValue(roiMap, key, caches = {}) {
  return resolveRoiVariableEntry(roiMap, key, caches).value;
}

export function isCheckboxCheckedValue(value) {
  return value === 1 || value === "1" || value === true;
}

export function evaluateCheckboxCondition(operator, variableValue) {
  const checked = isCheckboxCheckedValue(variableValue);
  if (operator === "is checked") {
    return checked;
  }
  if (operator === "is not checked") {
    return !checked;
  }
  return null;
}

/** Attach uuid + fieldId metadata and optional label for resolver + debug. */
export function decorateRoiEntry(entry, { uuid, fieldId, label, type } = {}) {
  return {
    ...entry,
    componentUuid:
      uuid !== undefined && uuid !== null && String(uuid).trim() !== ""
        ? String(uuid)
        : entry?.componentUuid || "",
    fieldId:
      fieldId !== undefined && fieldId !== null && String(fieldId).trim() !== ""
        ? String(fieldId)
        : entry?.fieldId || "",
    text: label || entry?.text || entry?.label || "",
    type: type || entry?.type || "",
  };
}

/** Keep uuid and fieldId keys in sync after user updates (radio, range, etc.). */
export function syncRoiVariableAliases(roiMap, primaryKey, patch = {}) {
  if (!roiMap || !primaryKey || !roiMap[primaryKey]) {
    return roiMap;
  }

  const base = { ...roiMap[primaryKey], ...patch };
  roiMap[primaryKey] = base;

  const aliasKeys = new Set();
  if (base.componentUuid && String(base.componentUuid) !== String(primaryKey)) {
    aliasKeys.add(String(base.componentUuid));
  }
  if (base.fieldId && String(base.fieldId) !== String(primaryKey)) {
    aliasKeys.add(String(base.fieldId));
  }

  aliasKeys.forEach((alias) => {
    roiMap[alias] = { ...(roiMap[alias] || {}), ...base };
  });

  return roiMap;
}

export function seedRoiVariableAlias(roiMap, primaryKey, aliasKey) {
  if (!roiMap || !primaryKey || !aliasKey || String(primaryKey) === String(aliasKey)) {
    return;
  }
  if (roiMap[primaryKey]) {
    roiMap[String(aliasKey)] = { ...roiMap[primaryKey] };
  }
}

export function shouldUseNumericCalculation(variableDataType, actionType, rawValue) {
  if (variableDataType === "text") {
    return false;
  }
  if (variableDataType === "number" || variableDataType === "currency") {
    if (
      actionType === "assign" &&
      typeof rawValue === "string" &&
      rawValue !== "" &&
      Number.isNaN(parseFloat(rawValue))
    ) {
      return false;
    }
    return true;
  }
  if (typeof rawValue === "number") {
    return true;
  }
  if (typeof rawValue === "string" && rawValue !== "" && !Number.isNaN(parseFloat(rawValue))) {
    return true;
  }
  return false;
}

export function resolveCalculateOperand({
  valueType,
  staticValue,
  sourceKey,
  roiMap,
  caches,
  variableDataType,
  actionType,
}) {
  let raw;
  let resolvedKey = null;

  if (valueType === "static") {
    raw = staticValue;
  } else {
    const resolved = resolveRoiVariableEntry(roiMap, sourceKey, caches);
    raw = resolved.value;
    resolvedKey = resolved.resolvedKey;
  }

  const isNumber = shouldUseNumericCalculation(variableDataType, actionType, raw);

  if (isNumber) {
    const n = parseFloat(raw);
    return {
      raw,
      coerced: Number.isNaN(n) ? 0 : n,
      isNumber: true,
      resolvedKey,
    };
  }

  return {
    raw,
    coerced: raw ?? "",
    isNumber: false,
    resolvedKey,
  };
}
